import React, { useState } from 'react';
import { useNotification } from '../hooks/useNotification';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Bell, Globe, DollarSign, Shield, ArrowLeft, Save } from 'lucide-react';
import { Link } from 'react-router-dom';

export const UserSettingsPage: React.FC = () => {
  const { showSuccess } = useNotification();
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    marketingOptIn: true,
    language: 'en',
    currency: 'USD',
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    showSuccess('Preferences updated successfully!');
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div className="space-y-2">
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-[#111418] dark:text-white uppercase leading-none">Console Settings</h1>
          <p className="text-gray-500 font-medium tracking-tight">Configure your notification protocols and global preferences.</p>
        </div>
        <Link to="/profile" className="text-sm font-bold text-primary flex items-center gap-2 hover:gap-3 transition-all mb-1">
          <ArrowLeft className="size-4" /> Account Identification
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
        <div className="lg:col-span-8">
          <form onSubmit={handleSave} className="space-y-12">
            {/* Section: Notifications */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <Bell className="size-5 text-primary" />
                <h2 className="text-xl font-black tracking-tight text-[#111418] dark:text-white uppercase">Notification Protocols</h2>
              </div>
              <div className="space-y-4">
                {[
                  { id: 'emailNotifications', label: 'Email Transmissions', desc: 'Receive secure updates via encrypted email.' },
                  { id: 'smsNotifications', label: 'Cellular Alerts', desc: 'Receive high-priority SMS notifications.' },
                  { id: 'marketingOptIn', label: 'Insider Intelligence', desc: 'Be first to know about seasonal acquisitions.' }
                ].map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-6 bg-gray-50 dark:bg-gray-900 rounded-[1.5rem] border border-border/40 hover:border-primary/20 transition-all group">
                    <div className="space-y-1">
                      <p className="text-sm font-black text-[#111418] dark:text-white uppercase tracking-tight">{item.label}</p>
                      <p className="text-xs text-gray-400 font-medium">{item.desc}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, [item.id]: !settings[item.id as keyof typeof settings] })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings[item.id as keyof typeof settings] ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-800'}`}
                    >
                      <span className={`inline-block size-4 transform rounded-full bg-white transition-transform ${settings[item.id as keyof typeof settings] ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <Separator className="opacity-50" />

            {/* Section: Localization */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <Globe className="size-5 text-primary" />
                <h2 className="text-xl font-black tracking-tight text-[#111418] dark:text-white uppercase">Regional Localization</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-2">
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2 px-1">
                    <Globe className="size-3" /> Dialect
                  </label>
                  <select
                    value={settings.language}
                    onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                    className="w-full h-14 bg-gray-50 dark:bg-gray-900 border-none rounded-2xl px-6 font-bold focus:ring-2 focus:ring-primary/20 appearance-none text-sm"
                  >
                    <option value="en">English (Global)</option>
                    <option value="es">Español</option>
                    <option value="fr">Français</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center gap-2 px-1">
                    <DollarSign className="size-3" /> Currency
                  </label>
                  <select
                    value={settings.currency}
                    onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                    className="w-full h-14 bg-gray-50 dark:bg-gray-900 border-none rounded-2xl px-6 font-bold focus:ring-2 focus:ring-primary/20 appearance-none text-sm"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
              </div>
            </section>

            <div className="pt-6">
              <Button type="submit" className="h-16 px-12 rounded-2xl font-black bg-primary hover:bg-primary/90 text-white shadow-2xl shadow-primary/20 transition-all hover:-translate-y-1 flex items-center gap-3">
                <Save className="size-5" /> Synchronize Preferences
              </Button>
            </div>
          </form>
        </div>

        {/* Sidebar Info */}
        <div className="lg:col-span-4">
          <div className="p-10 bg-gray-50 dark:bg-gray-900 rounded-[2.5rem] border border-border/40 space-y-6 sticky top-24">
            <div className="size-14 bg-white dark:bg-black rounded-2xl flex items-center justify-center shadow-sm">
              <Shield className="size-7 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-black tracking-tight text-[#111418] dark:text-white uppercase leading-tight">Privacy Vault</h3>
              <p className="text-xs font-medium text-gray-400 leading-relaxed">Your data is governed by sovereign privacy laws. We use industry-standard encryption for all configuration changes.</p>
            </div>
            <Separator className="opacity-50" />
            <div className="space-y-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-500">Security: Optimal</p>
              <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">Last Sync: 2 minutes ago</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
